import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { Card, PageHeader } from '@/components/ui/page';
import { SegmentChip } from '@/components/ui/segment-chip';
import { btn, field } from '@/components/ui/styles';
import { SEGMENT_COLORS, slotColor } from '@/constants/segment-colors';
import { textOn } from '@/utils/color';
import { formatDate, leadTitle } from '@/utils/format';
import http from '@/utils/http';
import { stageLabel } from '@/utils/labels';
import { useApi, useDebounced } from '@/utils/use-api';
import { forgetSegments, useSegments } from '@/utils/use-segments';

import type { SegmentDetail, SegmentList, SegmentPreview } from './api-types';
import { FIELDS_BY_KIND, KIND_LABEL, OP_WORD, RULE_FIELDS, countryLabel, countryOptions, fromApi, newRule, toApi, type Op, type Rule, type RuleField } from './rules';

// 44px tall with 16px text on a phone (UI-1), so iOS does not zoom in on focus.
const numberCls =
  'h-11 w-28 rounded-lg bg-control px-3 text-base tabular-nums text-fg shadow-ctl focus:outline-none focus:ring-2 focus:ring-brand/50 md:h-9 md:text-sm';

/** One condition: the field, how it compares, the value, and any open question about it. */
const RuleRow = ({ rule, onChange, onRemove }: { rule: Rule; onChange: (r: Rule) => void; onRemove: () => void }) => {
  const def = RULE_FIELDS[rule.field];
  const values = rule.value.split(',').filter(Boolean);
  const { items } = useSegments();
  const options = rule.field === 'country' ? countryOptions(items, values) : def.options;
  const toggle = (v: string) =>
    onChange({ ...rule, value: (values.includes(v) ? values.filter((x) => x !== v) : [...values, v]).join(',') });

  return (
    <li className="rounded-lg bg-surface-inset p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="basis-full text-sm text-fg-strong sm:basis-auto">{def.label}</span>
        {def.ops.length > 1 ? (
          <select
            aria-label={`${def.label}: comparison`}
            className={field.select}
            value={rule.op}
            onChange={(e) => onChange({ ...rule, op: e.target.value as Op })}
          >
            {def.ops.map((op) => (
              <option key={op} value={op}>
                {OP_WORD[op]}
              </option>
            ))}
          </select>
        ) : (
          <span className="text-sm text-fg-muted">{OP_WORD[rule.op]}</span>
        )}
        {def.input === 'number' && (
          <span className="flex items-center gap-1.5">
            <input
              type="number"
              min={0}
              aria-label={`${def.label}: value`}
              className={numberCls}
              value={rule.value}
              onChange={(e) => onChange({ ...rule, value: e.target.value })}
            />
            {def.unit && <span className="text-sm text-fg-muted">{def.unit}</span>}
          </span>
        )}
        {def.input === 'choice' && !def.multi && (
          <select
            aria-label={`${def.label}: value`}
            className={field.select}
            value={rule.value}
            onChange={(e) => onChange({ ...rule, value: e.target.value })}
          >
            {options?.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        )}
        <button type="button" onClick={onRemove} aria-label={`Remove ${def.label}`} className={classNames(btn.quiet, 'ml-auto')}>
          Remove
        </button>
      </div>
      {def.multi && (
        <div className="mt-2 flex flex-wrap gap-2 md:gap-1.5" role="group" aria-label={`${def.label}: values`}>
          {options?.map((o) => {
            const on = values.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                aria-pressed={on}
                onClick={() => toggle(o.value)}
                className={classNames(
                  'pill inline-flex min-h-11 items-center rounded-pill px-3 text-sm transition-colors md:min-h-0 md:px-2.5 md:py-1 md:text-xs',
                  on ? 'text-brand [--tag-color:theme(colors.brand.DEFAULT/15%)]' : 'text-fg-muted hover:text-fg',
                )}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      )}
      {def.note && <p className="mt-2 text-xs text-fg-muted">{def.note}</p>}
    </li>
  );
};

/**
 * What the rules pick right now (POST /segments/preview), linking each casino and
 * lead to its page. Performance rules run on the metrics source (the stats service; mock locally).
 */
const MatchCard = ({ rules }: { rules: Rule[] }) => {
  const key = useDebounced(JSON.stringify(toApi(rules)), 300);
  const [preview, setPreview] = useState<SegmentPreview | null>(null);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    let stale = false;
    http
      .post<SegmentPreview>('/segments/preview', { rules: JSON.parse(key) })
      .then((r) => {
        if (stale) return;
        setPreview(r.data);
        setInvalid(false);
      })
      .catch(() => !stale && setInvalid(true));
    return () => {
      stale = true;
    };
  }, [key]);

  const usesMetrics = rules.some((r) => RULE_FIELDS[r.field].kind === 'performance' && !RULE_FIELDS[r.field].pending);
  return (
    <Card
      title="Matching now"
      aside={
        preview?.metrics_source === 'mock' &&
        usesMetrics && (
          <DemoPill title="GGR, players and velocity come from the local mock metrics source.">Demo</DemoPill>
        )
      }
    >
      {invalid && <p className="mb-3 text-xs text-danger">A condition needs a value before it can be counted.</p>}
      <div className={classNames('grid grid-cols-2 gap-3 transition-opacity', !preview && 'opacity-50')}>
        <div>
          <p className="text-2xl font-semibold tabular-nums text-fg-strong">{preview?.casinos ?? '–'}</p>
          <p className="text-xs text-fg-muted">casinos</p>
        </div>
        <div>
          <p className="text-2xl font-semibold tabular-nums text-fg-strong">{preview?.leads ?? '–'}</p>
          <p className="text-xs text-fg-muted">leads</p>
        </div>
      </div>
      {preview && preview.skipped > 0 && (
        <p className="mt-3 text-xs text-fg-muted">
          {preview.skipped === 1 ? 'One condition is' : `${preview.skipped} conditions are`} not counted: no data for it yet.
        </p>
      )}
      <h3 className="mb-1.5 mt-4 text-xs uppercase tracking-wide text-fg-muted">Casinos</h3>
      {preview?.sample.casinos.length ? (
        <ul className="space-y-1 text-sm">
          {preview.sample.casinos.map((c) => (
            <li key={c.id} className="flex items-baseline justify-between gap-2">
              <Link to={`/casinos/${encodeURIComponent(c.id)}`} className="block truncate py-3 text-fg hover:text-brand md:py-0">
                {c.brand || 'Unnamed casino'}
              </Link>
              <span className="shrink-0 text-xs text-fg-faint">{c.country ? countryLabel(c.country) : '—'}</span>
            </li>
          ))}
          {preview.casinos > preview.sample.casinos.length && (
            <li className="text-xs text-fg-faint">and {preview.casinos - preview.sample.casinos.length} more</li>
          )}
        </ul>
      ) : (
        <p className="text-sm text-fg-faint">None.</p>
      )}
      <h3 className="mb-1.5 mt-4 text-xs uppercase tracking-wide text-fg-muted">Leads</h3>
      {preview?.sample.leads.length ? (
        <ul className="space-y-1 text-sm">
          {preview.sample.leads.map((l) => (
            <li key={l.id} className="flex items-baseline justify-between gap-2">
              <Link to={`/leads/${encodeURIComponent(l.id)}`} className="block truncate py-3 text-fg hover:text-brand md:py-0">
                {leadTitle(l.name, l.email)}
              </Link>
              <span className="shrink-0 text-xs text-fg-faint">{stageLabel(l.stage)}</span>
            </li>
          ))}
          {preview.leads > preview.sample.leads.length && (
            <li className="text-xs text-fg-faint">and {preview.leads - preview.sample.leads.length} more</li>
          )}
        </ul>
      ) : (
        <p className="text-sm text-fg-faint">None.</p>
      )}
    </Card>
  );
};

const Check = () => (
  <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3.5 8.5l3 3 6-7" />
  </svg>
);

/** The segment's colour: one of the presets or any other, with its tag as every page shows it. */
const ColorField = ({ value, onChange, name }: { value: string; onChange: (color: string) => void; name: string }) => {
  const custom = !SEGMENT_COLORS.some((c) => c.value === value);
  // 44px to tap on a phone (UI-1); the colour itself is a smaller dot inside.
  const target = 'relative flex h-11 w-11 items-center justify-center rounded-full md:h-8 md:w-8';
  const dot = 'flex h-7 w-7 items-center justify-center rounded-full md:h-6 md:w-6';
  return (
    <div className="mt-3">
      <span className={field.label}>Colour</span>
      <div role="radiogroup" aria-label="Colour" className="-mx-1.5 flex flex-wrap items-center md:mx-0 md:gap-1">
        {SEGMENT_COLORS.map((c) => (
          <button
            key={c.value}
            type="button"
            role="radio"
            aria-checked={value === c.value}
            aria-label={c.label}
            title={c.label}
            onClick={() => onChange(c.value)}
            className={classNames(target, 'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/50')}
          >
            <span className={classNames(dot, textOn(c.value))} style={{ backgroundColor: c.value }}>
              {value === c.value && <Check />}
            </span>
          </button>
        ))}
        <label title="Any other colour" className={classNames(target, 'cursor-pointer has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-brand/50')}>
          <input
            type="color"
            aria-label="Any other colour"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          />
          <span className={classNames(dot, custom ? textOn(value) : 'bg-hue-wheel')} style={custom ? { backgroundColor: value } : undefined}>
            {custom && <Check />}
          </span>
        </label>
      </div>
      <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
        <SegmentChip name={name.trim() || 'New segment'} color={value} />
        Its tag on every page. The text is white, or black where white would be hard to read.
      </p>
    </div>
  );
};

/**
 * One segment (SEG-1…3): its name and the rules a casino or lead must all meet.
 * Performance rules come from the metrics source (SEG-1); geography and type from
 * Owner Hub (SEG-2).
 */
const SegmentEditor = ({ existing, nextSlot }: { existing?: SegmentDetail; nextSlot: number }) => {
  const navigate = useNavigate();
  const [saved, setSaved] = useState(existing);
  // A country's own segment (SEG-2): Prisma made it and keeps its name and rule.
  const auto = Boolean(saved?.country);
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [color, setColor] = useState(existing?.color ?? slotColor(nextSlot));
  const [rules, setRules] = useState<Rule[]>(() => fromApi(existing?.rules ?? []));
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ text: string; error?: boolean } | null>(null);

  const edit = <T,>(set: (v: T) => void) => (v: T) => {
    set(v);
    setStatus(null);
  };
  const setRulesEdited = edit(setRules);

  const failure = (err: unknown) => {
    const code = (err as { response?: { status?: number } }).response?.status;
    setStatus({
      error: true,
      text: code === 409 ? 'Another segment already has this name.' : code === 422 ? 'Check the name and that every condition has a value.' : 'That did not work. Try again.',
    });
  };

  const save = async () => {
    if (!name.trim()) {
      setStatus({ text: 'Give the segment a name first.', error: true });
      return;
    }
    setBusy(true);
    const body = { name, description, color, rules: toApi(rules) };
    try {
      if (saved) {
        const r = await http.put<SegmentDetail>(`/segments/${encodeURIComponent(saved.id)}`, body);
        forgetSegments();
        setSaved(r.data);
        setStatus({ text: 'Saved.' });
      } else {
        const r = await http.post<SegmentDetail>('/segments', body);
        forgetSegments();
        navigate(`/segments/${encodeURIComponent(r.data.id)}`, { replace: true });
      }
    } catch (err) {
      failure(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!saved || !window.confirm(`Delete “${saved.name}”? Drafts, scheduled messages and triggers that target it lose it; a trigger left with no segment is switched off.`)) return;
    setBusy(true);
    try {
      await http.delete(`/segments/${encodeURIComponent(saved.id)}`);
      forgetSegments();
      navigate('/segments', { state: { deleted: saved.name } });
    } catch (err) {
      failure(err);
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={name.trim() || saved?.name || 'New segment'}
        description={
          saved
            ? `Updated ${formatDate(saved.updated_at)}${saved.updated_by ? ` by ${saved.updated_by}` : ''}. A casino or lead is in the segment when it meets every rule.`
            : 'A casino or lead is in the segment when it meets every rule. Messages target segments (SEG-3).'
        }
        back={{ to: '/segments', label: 'Segments' }}
        actions={
          <span className="hidden items-center gap-2 md:flex">
            {saved && !auto && (
              <button type="button" className={btn.danger} onClick={remove} disabled={busy}>
                Delete
              </button>
            )}
            <button type="button" className={btn.primary} onClick={save} disabled={busy}>
              Save
            </button>
          </span>
        }
      />
      {status && (
        <p role="status" className={classNames('mb-4 hidden text-sm md:block', status.error ? 'text-danger' : 'text-fg-muted')}>
          {status.text}
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4 md:space-y-6">
          <Card title="Details">
            <label className="block">
              <span className={field.label}>Name</span>
              <input
                className={classNames(field.input, 'disabled:cursor-not-allowed disabled:opacity-60')}
                value={name}
                maxLength={80}
                disabled={auto}
                placeholder="For example: Brazil, crypto"
                onChange={(e) => edit(setName)(e.target.value)}
              />
            </label>
            {auto && (
              <p className="mt-2 text-xs text-fg-muted">
                Prisma makes one segment for each country a casino or lead is in, and keeps its name and rule; it can’t be deleted. Its description and
                colour are yours. To group countries into a region, make a segment of your own with the Country condition.
              </p>
            )}
            <label className="mt-3 block">
              <span className={field.label}>Description</span>
              <textarea
                rows={2}
                maxLength={500}
                className={field.textarea}
                value={description}
                placeholder="Who is in it, in one line"
                onChange={(e) => edit(setDescription)(e.target.value)}
              />
            </label>
            <ColorField value={color} onChange={edit(setColor)} name={name} />
          </Card>

          <Card title="Rules" aside={<span className="text-xs text-fg-muted">A casino or lead must meet every condition</span>}>
            <fieldset disabled={auto} className={classNames('min-w-0', auto && 'cursor-not-allowed opacity-60')}>
              {rules.length === 0 && <p className="mb-4 text-sm text-fg-faint">No conditions yet: the segment matches everyone.</p>}
              {FIELDS_BY_KIND.map(({ kind }) => {
                const group = rules.filter((r) => RULE_FIELDS[r.field].kind === kind);
                if (group.length === 0) return null;
                return (
                  <div key={kind} className="mb-4">
                    <h3 className="mb-2 text-xs uppercase tracking-wide text-fg-muted">{KIND_LABEL[kind]}</h3>
                    <ul className="space-y-2">
                      {group.map((r) => (
                        <RuleRow
                          key={r.id}
                          rule={r}
                          onChange={(next) => setRulesEdited(rules.map((x) => (x.id === r.id ? next : x)))}
                          onRemove={() => setRulesEdited(rules.filter((x) => x.id !== r.id))}
                        />
                      ))}
                    </ul>
                  </div>
                );
              })}
              <label className="block md:inline-block">
                <span className="sr-only">Add a condition</span>
                <select
                  className={classNames(field.select, 'w-full md:w-auto')}
                  value=""
                  onChange={(e) => e.target.value && setRulesEdited([...rules, newRule(e.target.value as RuleField)])}
                >
                  <option value="">Add a condition…</option>
                  {FIELDS_BY_KIND.map(({ kind, fields }) => (
                    <optgroup key={kind} label={KIND_LABEL[kind]}>
                      {fields.map((f) => (
                        <option key={f} value={f}>
                          {RULE_FIELDS[f].label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
            </fieldset>
          </Card>
        </div>

        <div className="space-y-4 md:space-y-6">
          <MatchCard rules={rules} />
        </div>
      </div>

      {/* On a phone, Save stays in reach above the bottom tabs (UI-2). */}
      <div className="sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-20 -mx-4 mt-4 bg-surface px-4 py-3 shadow-up md:hidden">
        {status && (
          <p role="status" className={classNames('mb-2 text-sm', status.error ? 'text-danger' : 'text-fg-muted')}>
            {status.text}
          </p>
        )}
        <div className="flex gap-2">
          {saved && !auto && (
            <button type="button" className={classNames(btn.danger, 'flex-1')} onClick={remove} disabled={busy}>
              Delete
            </button>
          )}
          <button type="button" className={classNames(btn.primary, 'flex-1')} onClick={save} disabled={busy}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

/** /segments/new and /segments/:id. Keyed by id so moving between segments starts fresh. */
const SegmentEditorPage = () => {
  const { id } = useParams();
  const one = useApi<SegmentDetail>(id ? `/segments/${encodeURIComponent(id)}` : null);
  const list = useApi<SegmentList>(id ? null : '/segments');
  const back = { to: '/segments', label: 'Segments' };

  if (id && one.error === 'Not found.') {
    return (
      <div>
        <PageHeader title="Segment" back={back} />
        <StateMessage title="No such segment">
          It may have been deleted.{' '}
          <Link to="/segments" className={btn.link}>
            Back to segments
          </Link>
        </StateMessage>
      </div>
    );
  }
  const error = id ? one.error : list.error;
  if (error) {
    return (
      <div>
        <PageHeader title="Segment" back={back} />
        <StateMessage title="The segment could not be loaded" tone="error">
          {error}
        </StateMessage>
      </div>
    );
  }
  if (id ? !one.data : !list.data) {
    return (
      <div>
        <PageHeader title="Loading…" back={back} />
        <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
      </div>
    );
  }

  // A new segment starts with the lowest free slot's colour, as the backend gives it; grey once all six are taken.
  const used = new Set((list.data?.items ?? []).map((s) => s.slot));
  const nextSlot = [1, 2, 3, 4, 5, 6].find((s) => !used.has(s)) ?? 0;
  return <SegmentEditor key={id ?? 'new'} existing={one.data ?? undefined} nextSlot={nextSlot} />;
};

export default SegmentEditorPage;
